import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTheme } from '@/hoc';
import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '..';
import { CloseIcon } from '@/icons';
const ModalPreview = ({ title = '', contentWidth = 'w-full max-w-lg', containerPadding = '', children, isClosable = true, footer, headerActions, headerClassName = '', }) => {
    return (_jsx("div", { className: 'relative w-full h-full min-h-[300px] flex items-center justify-center bg-gray-100 p-4 rounded-lg', children: _jsxs("div", { className: `relative flex flex-col rounded-lg bg-white shadow-lg ${contentWidth} ${containerPadding}`, children: [_jsxs("div", { className: `flex items-center justify-between border-b border-gray-200 ${headerClassName}`, children: [_jsx("h2", { className: 'text-xl font-semibold text-gray-900', children: title }), _jsxs("div", { className: 'flex gap-4', children: [headerActions, isClosable && (_jsx(Button, { variant: 'text', className: 'rounded-full px-0 py-0 text-vjwPrimary cursor-default', "aria-label": 'Close modal preview', children: _jsx(CloseIcon, {}) }))] })] }), _jsx("div", { className: 'h-full overflow-y-auto', children: children }), footer ? (footer) : (_jsx("div", { className: 'mt-6 px-6 flex justify-end space-x-2', children: _jsx("button", { className: 'rounded-md bg-vjwSecondary px-4 py-2 text-sm font-medium text-gray-600 cursor-default', children: "Close" }) }))] }) }));
};
export const Modal = ({ isOpen, onClose, title = '', contentWidth = 'w-full max-w-lg', modelPadding = 'p-4', children, isClosable = true, footer, headerActions, headerClassName = '', containerPadding = '', modalRef, closeOnOutsideClick = false, isPreview = false, }) => {
    const { theme } = useTheme();
    const containerRef = useRef(null);
    const modalRoot = useMemo(() => {
        const modalRoot = document.getElementById('modal-root') || document.createElement('div');
        if (!modalRoot.parentElement) {
            modalRoot.id = 'modal-root';
            document.documentElement.appendChild(modalRoot);
        }
        containerRef.current = modalRoot;
        return modalRoot;
    }, []);
    useEffect(() => {
        const container = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
        if (!container)
            return;
        const setThemeProperties = (obj, prefix = '') => {
            Object.entries(obj).forEach(([key, value]) => {
                if (typeof value === 'object' && value !== null) {
                    setThemeProperties(value, `${prefix}${key}-`);
                }
                else {
                    container.style.setProperty(`--${prefix}${key}`, String(value));
                }
            });
        };
        setThemeProperties(theme);
    }, [theme, containerRef === null || containerRef === void 0 ? void 0 : containerRef.current]);
    useEffect(() => {
        if (isOpen && !isPreview) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen, isPreview]);
    if (!isOpen)
        return null;
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && closeOnOutsideClick) {
            onClose();
        }
    };
    if (isPreview) {
        return (_jsx(ModalPreview, { isOpen: isOpen, onClose: onClose, title: title, contentWidth: contentWidth, containerPadding: containerPadding, isClosable: isClosable, footer: footer, headerActions: headerActions, headerClassName: headerClassName, children: children }));
    }
    return createPortal(_jsx("div", { className: `fixed inset-0 z-100050 flex items-center justify-center bg-black bg-opacity-50 ${modelPadding}`, onClick: handleBackdropClick, ref: modalRef, children: _jsxs("div", { className: `relative flex flex-col rounded-lg bg-white shadow-lg animate-slide-in-top vjwMd:max-h-[90vh] ${contentWidth} ${containerPadding}`, children: [_jsxs("div", { className: `flex items-center justify-between border-b border-gray-200 ${headerClassName}`, children: [_jsx("h2", { className: 'text-xl font-semibold text-gray-900', children: title }), _jsxs("div", { className: 'flex gap-4', children: [headerActions, isClosable && (_jsx(Button, { onClick: onClose, "aria-label": 'Close modal', variant: 'text', className: 'rounded-full px-0 py-0 text-vjwPrimary', children: _jsx(CloseIcon, {}) }))] })] }), _jsx("div", { className: 'h-full overflow-y-auto', children: children }), footer ? (footer) : (_jsx("div", { className: 'mt-6 px-6 flex justify-end space-x-2', children: _jsx("button", { onClick: onClose, className: 'rounded-md bg-vjwSecondary px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200', children: "Close" }) }))] }) }), modalRoot);
};
